import dayjs from "dayjs";

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

export const normalizeGrantTypes = (raw = {}) => {
  const value =
    raw.grantType ||
    raw.grantTypes ||
    raw.grant ||
    raw.grants ||
    [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

export const unwrapClientPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload || {};
  if (payload.clientId || payload.name || payload.clientName) return payload;
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return unwrapClientPayload(payload.data);
  }
  if (payload.result && typeof payload.result === "object" && !Array.isArray(payload.result)) {
    return unwrapClientPayload(payload.result);
  }
  return payload;
};

export const normalizeClient = (rawInput = {}) => {
  const raw = unwrapClientPayload(rawInput);
  const clientId = firstDefined(raw.clientId, raw.id, raw._id, "");
  const roles = Array.isArray(raw.roles) ? raw.roles.filter(Boolean).map(String) : [];
  const grantType = normalizeGrantTypes(raw);
  const redirectUri = Array.isArray(raw.redirectUri)
    ? raw.redirectUri.filter(Boolean).map(String)
    : [];

  return {
    ...raw,
    id: clientId,
    clientId,
    name: firstDefined(raw.name, raw.clientName, ""),
    grantType,
    roles,
    redirectUri,
    privacyPolicy: firstDefined(raw.privacyPolicy, ""),
    allowUserCreation: Boolean(raw.allowUserCreation),
    allowSelfUserCreation: Boolean(raw.allowSelfUserCreation),
    allowLoginViaOTP: Boolean(raw.allowLoginViaOTP),
    supportsRefreshToken: Boolean(raw.supportsRefreshToken),
    enabled: raw.enabled !== false,
    accessTokenExpiryInSecs: raw.accessTokenExpiryInSecs,
    refreshTokenExpiryInSecs: raw.refreshTokenExpiryInSecs,
    creationDate: firstDefined(
      raw.createdAt,
      raw.creationDate,
      raw.createdDate,
      raw.createdOn
    ),
    lastModifiedDate: firstDefined(
      raw.updatedAt,
      raw.lastModifiedDate,
      raw.lastModified
    ),
  };
};

export const normalizeClientList = (payload) => {
  const source = unwrapClientPayload(payload);
  if (Array.isArray(payload)) return payload.map(normalizeClient);
  if (Array.isArray(payload?.content)) return payload.content.map(normalizeClient);
  if (Array.isArray(payload?.data)) return payload.data.map(normalizeClient);
  if (Array.isArray(payload?.clients)) return payload.clients.map(normalizeClient);
  if (source && (source.clientId || source.id || source.name)) {
    return [normalizeClient(source)];
  }
  return [];
};

export const formatClientDate = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    const millis = numeric < 1e12 ? numeric * 1000 : numeric;
    const parsed = dayjs(millis);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : "—";
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : "—";
};

export const pickClientSecret = (payload = {}) =>
  firstDefined(
    payload.clientSecret,
    payload.secret,
    payload.token,
    payload.password,
    payload?.data?.clientSecret,
    payload?.data?.secret,
    payload?.data?.token
  );

export const matchesClientSearch = (client, filters = {}) => {
  const name = String(filters.name || "").trim().toLowerCase();
  if (name && !String(client.name || "").toLowerCase().includes(name)) {
    return false;
  }
  return true;
};
